import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Layout } from '../components/Layout';
import { FadeInUp, StaggerContainer, StaggerItem } from '../components/AnimatedSection';
import { Network, Factory, ShieldCheck, Truck, Brain, Maximize } from 'lucide-react';

const Microfactory = () => {
  const { t } = useLanguage();

  const features = [
    { key: 'network', icon: Network },
    { key: 'production', icon: Factory },
    { key: 'control', icon: ShieldCheck },
    { key: 'logistics', icon: Truck },
    { key: 'ai', icon: Brain },
    { key: 'scale', icon: Maximize }
  ];

  return (
    <Layout>
      {/* Hero */}
      <section className="py-24 bg-white" data-testid="microfactory-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <span className="confidential-badge mb-6 inline-block">
              {t('microfactory.badge')}
            </span>
          </FadeInUp>
          
          <FadeInUp delay={0.1}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy mb-6" data-testid="microfactory-title">
              {t('microfactory.title')}
            </h1>
          </FadeInUp>
          
          <FadeInUp delay={0.2}>
            <p className="text-lg md:text-xl text-[#0066CC] font-medium max-w-3xl mb-8">
              {t('microfactory.subtitle')}
            </p>
          </FadeInUp>

          <FadeInUp delay={0.3}>
            <p className="text-base md:text-lg text-slate-600 max-w-3xl leading-relaxed">
              {t('microfactory.intro')}
            </p>
          </FadeInUp>
        </div>
      </section>

      {/* Network Diagram */}
      <section className="py-20 bg-slate-50" data-testid="microfactory-diagram">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <h2 className="text-2xl md:text-3xl font-bold text-navy mb-12 text-center">
              {t('microfactory.diagram.title')}
            </h2>
          </FadeInUp>

          <FadeInUp delay={0.2}>
            <div className="relative max-w-4xl mx-auto">
              {/* Central Hub */}
              <div className="flex justify-center mb-12">
                <div className="w-32 h-32 bg-navy flex items-center justify-center">
                  <div className="text-center">
                    <Network className="w-8 h-8 text-white mx-auto mb-1" />
                    <p className="text-xs text-white uppercase tracking-wider">{t('microfactory.diagram.hub')}</p>
                  </div>
                </div>
              </div>

              {/* Connection Lines */}
              <div className="absolute top-32 left-1/2 w-px h-12 bg-slate-300 -translate-x-1/2" />
              
              {/* Regional Nodes */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {['Nord-Vest', 'Nord-Est', 'Sud', 'Vest'].map((region, index) => (
                  <div key={region} className="relative">
                    <div className="absolute -top-6 left-1/2 w-px h-6 bg-slate-300 -translate-x-1/2 hidden md:block" />
                    <div className="bg-white border border-slate-200 p-6 text-center hover:border-[#0066CC] transition-colors">
                      <Factory className="w-6 h-6 text-[#0066CC] mx-auto mb-2" />
                      <p className="text-sm font-semibold text-navy">{region}</p>
                      <p className="text-xs text-slate-500 mt-1">{t('microfactory.diagram.nodes')}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Digital Flow Label */}
              <div className="mt-12 text-center">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#0066CC] text-white text-xs uppercase tracking-wider">
                  <Brain className="w-4 h-4" />
                  {t('microfactory.diagram.flow')}
                </span>
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white" data-testid="microfactory-features">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" staggerDelay={0.1}>
            {features.map((feature) => {
              const IconComponent = feature.icon;
              return (
                <StaggerItem key={feature.key}>
                  <div 
                    className="border border-slate-200 p-8 hover:border-[#0066CC] transition-colors group h-full"
                    data-testid={`feature-${feature.key}`}
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-slate-100 group-hover:bg-[#0066CC] transition-colors mb-4">
                      <IconComponent className="w-6 h-6 text-slate-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-lg font-semibold text-navy mb-2">
                      {t(`microfactory.features.${feature.key}.title`)}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      {t(`microfactory.features.${feature.key}.desc`)}
                    </p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* Visual Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1581093586267-784d0a92223d?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200"
                alt="Industrial Automation"
                className="w-full aspect-[21/9] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-navy/80 to-transparent flex items-center">
                <div className="p-8 md:p-12 max-w-xl">
                  <p className="text-3xl md:text-4xl font-bold text-white mb-4">
                    Producție Locală
                  </p>
                  <p className="text-white/80">
                    Independență. Reziliență. Control Strategic.
                  </p>
                </div>
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>
    </Layout>
  );
};

export default Microfactory;
